import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { app, reportTeamsInitResult } from "./teamsInit";

interface TeamsContextType {
    isInTeams: boolean;
    teamsTheme: string | null;
}

const TeamsContext = createContext<TeamsContextType>({
    isInTeams: false,
    teamsTheme: null
});

interface TeamsProviderProps {
    children: ReactNode;
    onThemeChange?: (theme: "light" | "dark") => void;
}

export const TeamsProvider: React.FC<TeamsProviderProps> = ({ children, onThemeChange }) => {
    const [isInTeams, setIsInTeams] = useState(false);
    const [teamsTheme, setTeamsTheme] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;

        const applyTheme = (teamsThemeName: string) => {
            const mapped = teamsThemeName === "dark" || teamsThemeName === "contrast" ? "dark" : "light";
            document.documentElement.setAttribute("data-theme", mapped);
            if (onThemeChange) {
                onThemeChange(mapped);
            }
        };

        const initTeams = async () => {
            try {
                await app.initialize();

                // Report success so authConfig.ts can proceed with SSO token
                reportTeamsInitResult(true);

                if (!mounted) return;

                const context = await app.getContext();
                const theme = context.app?.theme || "default";
                setTeamsTheme(theme);
                applyTheme(theme);

                app.registerOnThemeChangeHandler((newTheme: string) => {
                    if (!mounted) return;
                    setTeamsTheme(newTheme);
                    applyTheme(newTheme);
                });

                app.notifySuccess();
                setIsInTeams(true);
            } catch {
                // Not running inside Teams
                reportTeamsInitResult(false);
                if (mounted) {
                    setIsInTeams(false);
                }
            }
        };

        initTeams();

        return () => {
            mounted = false;
        };
    }, [onThemeChange]);

    return (
        <TeamsContext.Provider value={{ isInTeams, teamsTheme }}>
            {children}
        </TeamsContext.Provider>
    );
};

export const useTeams = () => useContext(TeamsContext);
